import { dayTotals, scaleNutrients, type FoodEntry, type Meal, type NutrientTotals, type OccasionType } from './nutrition';

/**
 * ⭐ Nutrition — meal plans (phase 3a 2026-10-09; several named plans 2026-10-17, owner:
 * "create several eating plans, each one has its own meal list, Occasions, schedule …
 * activate any of them"). Pure helpers: which plan — and which Day of it — a date falls
 * on, which plan row decides an occasion, planned totals. The store reads and writes;
 * nothing here touches the network.
 *
 * A PLAN is Day 1 … Day N (`nutrition_plans`). A RUN says when it's active
 * (`nutrition_plan_runs`, insert-only): from `startDate`, repeating or until `endDate`;
 * a run with no plan is a stop. For a date, the NEWEST-made run that has started by then
 * decides it; past its end there is no plan. So starting a plan never edits an older run
 * and what was planned on a past day never changes.
 *
 * A plan ROW is a CHANGE, never edited (`nutrition_plan_entries`, insert-only):
 *   · day    — Day n of the plan, from `effectiveFrom`
 *   · date   — only on `planDate`
 *   · weekly — the old shape (every <weekday>), read as Day ((weekday + 6) % 7) + 1:
 *              exact for "My plan", whose Day 1 is always a Monday (mig 0299)
 * For an occasion on a date the NEWEST applicable row wins (by `createdAt`). A row with
 * no meal is an explicit "nothing planned".
 */

/** A named plan (`nutrition_plans`). */
export interface MealPlan {
  id: string;
  name: string;
  /** Its length: Day 1 … Day `days`. */
  days: number;
  archivedAt: string | null;
  createdAt: string;
}

/** When a plan is active (`nutrition_plan_runs`). `planId` null = no plan from `startDate`. */
export interface PlanRun {
  id: string;
  planId: string | null;
  startDate: string;
  repeats: boolean;
  /** The last day of a run that stops after N days. */
  endDate: string | null;
  createdAt: string;
}

/** One of a plan's named eating occasions (`nutrition_plan_occasions`). */
export interface PlanOccasion {
  id: string;
  name: string;
  position: number;
  archivedAt: string | null;
  /** What it's for (mig 0298) — planning it offers meals with this tag first. Null/absent = any meal. */
  kind?: OccasionType | null;
  /** Its plan (mig 0299). Null/absent = a row the old app wrote: the person's first plan. */
  planId?: string | null;
}

export interface PlanEntry {
  id: string;
  occasionId: string;
  scope: 'day' | 'date' | 'weekly';
  /** Day n of the plan (day rows). */
  dayIndex?: number | null;
  /** 0 = Sunday … 6 = Saturday (old weekly rows). */
  weekday: number | null;
  /** YYYY-MM-DD (date rows). */
  planDate: string | null;
  effectiveFrom: string;
  /** Null = "nothing planned". */
  mealId: string | null;
  portion: number | null;
  createdAt: string;
}

/** Everything the plan helpers read — the store's rows. */
export interface PlanState {
  plans: ReadonlyArray<MealPlan>;
  runs: ReadonlyArray<PlanRun>;
  occasions: ReadonlyArray<PlanOccasion>;
  entries: ReadonlyArray<PlanEntry>;
}

export const STARTER_OCCASIONS = ['Breakfast', 'Lunch', 'Dinner'] as const;
export const OCCASION_NAME_MAX = 40;
export const PLAN_NAME_MAX = 60;
export const PLAN_DAYS_MAX = 90;
export const PLAN_PORTION_MAX = 20;

/** Day of the week of a local YYYY-MM-DD (0 = Sunday), independent of the device time zone. */
export function weekdayOf(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Add days to a YYYY-MM-DD (calendar arithmetic, no time zone). */
export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The Monday that starts the week containing `ymd`. */
export function weekStart(ymd: string): string {
  return addDays(ymd, -((weekdayOf(ymd) + 6) % 7));
}

/** The seven dates Monday → Sunday from a week start. */
export function weekDays(start: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Occasions in your order, archived ones left out. */
export function activeOccasions(occasions: ReadonlyArray<PlanOccasion>): PlanOccasion[] {
  return occasions.filter(o => !o.archivedAt).sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

/** Days from `a` to `b` (YYYY-MM-DD, calendar arithmetic). */
export function daysBetween(a: string, b: string): number {
  const t = (ymd: string) => { const [y, m, d] = ymd.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  return Math.round((t(b) - t(a)) / 86400000);
}

/** The person's first plan — where an occasion the old app wrote (no plan) belongs. */
export function firstPlan(plans: ReadonlyArray<MealPlan>): MealPlan | null {
  return [...plans].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))[0] ?? null;
}

/** The plan an occasion belongs to. */
export function planIdOf(o: PlanOccasion, plans: ReadonlyArray<MealPlan>): string | null {
  return o.planId ?? firstPlan(plans)?.id ?? null;
}

/** A plan's occasions in its order, archived ones left out. */
export function occasionsOf(state: Pick<PlanState, 'plans' | 'occasions'>, planId: string | null): PlanOccasion[] {
  return planId ? activeOccasions(state.occasions.filter(o => planIdOf(o, state.plans) === planId)) : [];
}

/** The run deciding a date: the newest-made run that has started by then (it may be a stop, or over). */
export function runOn(runs: ReadonlyArray<PlanRun>, date: string): PlanRun | null {
  let best: PlanRun | null = null;
  for (const r of runs) {
    if (r.startDate > date) continue;
    if (!best || r.createdAt > best.createdAt || (r.createdAt === best.createdAt && r.id > best.id)) best = r;
  }
  return best;
}

/** A date on a plan: which plan, its run, which Day (1…N) and which time round (cycle 1, 2 …). */
export interface PlanDay { plan: MealPlan; run: PlanRun; day: number; cycle: number }

/** Which plan — and which Day of it — a date falls on; null = no plan that day. */
export function planDayOn(state: Pick<PlanState, 'plans' | 'runs'>, date: string): PlanDay | null {
  const run = runOn(state.runs, date);
  if (!run?.planId || (run.endDate && date > run.endDate)) return null;
  const plan = state.plans.find(p => p.id === run.planId);
  if (!plan || plan.days < 1) return null;
  const n = daysBetween(run.startDate, date);
  if (n < 0 || (!run.repeats && n >= plan.days)) return null;
  return { plan, run, day: (n % plan.days) + 1, cycle: Math.floor(n / plan.days) + 1 };
}

/** A row's Day: day rows say it; an old weekly row is Monday-based (exact for "My plan"). */
export function dayOfEntry(e: PlanEntry): number | null {
  if (e.scope === 'day') return e.dayIndex ?? null;
  if (e.scope === 'weekly' && e.weekday !== null) return ((e.weekday + 6) % 7) + 1;
  return null;
}

/** Does this row speak for `date`, which is Day `day` of the row's plan (null = not on it — then nothing does)? */
function applies(e: PlanEntry, date: string, day: number | null): boolean {
  if (day === null) return false;
  if (e.scope === 'date') return e.planDate === date;
  return dayOfEntry(e) === day && e.effectiveFrom <= date;
}

/** The row deciding an occasion on a date (Day `day` of its plan) — the newest applicable one — or null. */
export function resolvePlanRow(entries: ReadonlyArray<PlanEntry>, occasionId: string, date: string, day: number | null): PlanEntry | null {
  let best: PlanEntry | null = null;
  for (const e of entries) {
    if (e.occasionId !== occasionId || !applies(e, date, day)) continue;
    if (!best || e.createdAt > best.createdAt || (e.createdAt === best.createdAt && e.id > best.id)) best = e;
  }
  return best;
}

/** What's planned for an occasion on a date: a meal and portion, or null (nothing). */
export function plannedFor(entries: ReadonlyArray<PlanEntry>, occasionId: string, date: string, day: number | null): { mealId: string; portion: number } | null {
  const row = resolvePlanRow(entries, occasionId, date, day);
  return row && row.mealId && row.portion ? { mealId: row.mealId, portion: row.portion } : null;
}

/** A planned meal, resolved against your meals (a meal not found is reported, never guessed). */
export interface PlannedItem {
  occasion: PlanOccasion;
  mealId: string;
  portion: number;
  meal: Meal | null;
}

/** Every planned meal on a date — from the plan that date falls on — in its occasion order. */
export function plannedOn(state: PlanState, meals: ReadonlyArray<Meal>, date: string): PlannedItem[] {
  const pd = planDayOn(state, date);
  if (!pd) return [];
  const byId = new Map(meals.map(m => [m.id, m]));
  const out: PlannedItem[] = [];
  for (const occasion of occasionsOf(state, pd.plan.id)) {
    const p = plannedFor(state.entries, occasion.id, date, pd.day);
    if (p) out.push({ occasion, mealId: p.mealId, portion: p.portion, meal: byId.get(p.mealId) ?? null });
  }
  return out;
}

/** The occasions a date's plan has (none when no plan is active that day). */
export function occasionsOn(state: PlanState, date: string): PlanOccasion[] {
  const pd = planDayOn(state, date);
  return pd ? occasionsOf(state, pd.plan.id) : [];
}

/**
 * A day's planned totals — kept apart from what was eaten, never added in. A planned meal
 * with an unknown nutrient (or a meal that can't be found) marks that total incomplete.
 */
export function plannedTotals(items: ReadonlyArray<PlannedItem>): NutrientTotals {
  const pseudo: FoodEntry[] = items.map((it, i) => (
    it.meal
      ? { id: String(i), name: it.meal.name, at: '', ...scaleNutrients(it.meal.nutrients, it.portion) }
      : { id: String(i), name: '', at: '', calories: null, protein: null, carbs: null, fat: null, fibre: null }
  ));
  return dayTotals(pseudo);
}

/** Plans are made for today onward; past days are history. */
export function canPlanOn(date: string, today: string): boolean {
  return date >= today;
}

/** An occasion name as saved: trimmed, never blank, at most 40 characters. */
export function cleanOccasionName(raw: string): string | null {
  const t = raw.trim().slice(0, OCCASION_NAME_MAX);
  return t ? t : null;
}

/** A plan name as saved: trimmed, never blank, at most 60 characters. */
export function cleanPlanName(raw: string): string | null {
  const t = raw.trim().slice(0, PLAN_NAME_MAX);
  return t ? t : null;
}

/** Two plan names count as the same when they match ignoring case and outer spaces (the database's rule). */
export const samePlanName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The plan to show first: the one today falls on, else the newest live plan, else none. */
export function planInView(state: Pick<PlanState, 'plans' | 'runs'>, today: string): MealPlan | null {
  return planDayOn(state, today)?.plan
    ?? [...state.plans].filter(p => !p.archivedAt).sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id))[0]
    ?? null;
}

/** A run made to start after today that will decide its start day (the newest-made run), if any. */
export function upcomingRun(runs: ReadonlyArray<PlanRun>, today: string): PlanRun | null {
  let newest: PlanRun | null = null;
  for (const r of runs) if (!newest || r.createdAt > newest.createdAt || (r.createdAt === newest.createdAt && r.id > newest.id)) newest = r;
  return newest && newest.startDate > today ? newest : null;
}

/** Has this plan any run at all (started or scheduled)? */
export const hasRun = (runs: ReadonlyArray<PlanRun>, planId: string) => runs.some(r => r.planId === planId);

/** Has this plan ever STARTED (a run on or before today)? Then it can only be archived, never deleted. */
export const hasStarted = (runs: ReadonlyArray<PlanRun>, planId: string, today: string) => runs.some(r => r.planId === planId && r.startDate <= today);

/** Where a plan stands today. */
export type PlanStatus =
  /** `until`: its last day when another plan (or a stop) — `next` — is scheduled to take over. */
  | { kind: 'active'; run: PlanRun; day: number; cycle: number; until?: string; next?: PlanRun }
  | { kind: 'scheduled'; run: PlanRun }
  | { kind: 'ended'; lastDate: string }
  | { kind: 'never' };

export function planStatus(state: Pick<PlanState, 'plans' | 'runs'>, planId: string, today: string): PlanStatus {
  const pd = planDayOn(state, today);
  const next = upcomingRun(state.runs, today);
  if (pd?.plan.id === planId) {
    const until = next && next.planId !== planId ? addDays(next.startDate, -1) : undefined;
    const end = pd.run.endDate && (!until || pd.run.endDate <= until) ? undefined : until;
    return { kind: 'active', run: pd.run, day: pd.day, cycle: pd.cycle, ...(end ? { until: end, next: next! } : {}) };
  }
  if (next?.planId === planId) return { kind: 'scheduled', run: next };
  // Ended: the last day it decided before today (walk back over its runs' spans).
  let last: string | null = null;
  for (const r of state.runs) {
    if (r.planId !== planId || r.startDate > today) continue;
    const from = r.endDate && r.endDate < today ? r.endDate : addDays(today, -1);
    for (let d = from; d >= r.startDate; d = addDays(d, -1)) {
      if (planDayOn(state, d)?.run.id === r.id) { if (!last || d > last) last = d; break; }
    }
  }
  return last ? { kind: 'ended', lastDate: last } : { kind: 'never' };
}

/** One grid column: Day n of the plan, on a real date when the plan has one there. */
export interface PlanColumn { day: number | null; date: string | null }

/** The run the plan's dates come from: its running run, else its scheduled one. */
export function datedRun(state: Pick<PlanState, 'plans' | 'runs'>, planId: string, today: string): PlanRun | null {
  const s = planStatus(state, planId, today);
  return s.kind === 'active' || s.kind === 'scheduled' ? s.run : null;
}

/** How many 7-column windows a plan has: dated (its run, up to a year ahead) or by Day (Days 1–7, 8–14 …). */
export function planWindowCount(state: Pick<PlanState, 'plans' | 'runs'>, plan: MealPlan, today: string): number {
  const s = planStatus(state, plan.id, today);
  const run = s.kind === 'active' || s.kind === 'scheduled' ? s.run : null;
  if (!run) return Math.max(1, Math.ceil(plan.days / 7));
  // Up to its end, the day before another plan takes over, or a year ahead.
  const last = (s.kind === 'active' && s.until) || run.endDate || addDays(today, 365);
  return Math.max(1, Math.floor(daysBetween(run.startDate, last) / 7) + 1);
}

/** The window to open on: the one holding today on a running plan, else the first. */
export function planStartWindow(state: Pick<PlanState, 'plans' | 'runs'>, plan: MealPlan, today: string): number {
  const s = planStatus(state, plan.id, today);
  return s.kind === 'active' ? Math.floor(daysBetween(s.run.startDate, today) / 7) : 0;
}

/**
 * Window `w`'s seven columns. A dated plan shows its run's dates (a date the run no longer
 * decides — another plan or a stop took over — has no Day); otherwise Days 7w+1 … 7w+7.
 */
export function planColumns(state: Pick<PlanState, 'plans' | 'runs'>, plan: MealPlan, today: string, w: number): PlanColumn[] {
  const run = datedRun(state, plan.id, today);
  if (!run) {
    const first = 7 * w + 1;
    return Array.from({ length: Math.max(0, Math.min(7, plan.days - first + 1)) }, (_, i) => ({ day: first + i, date: null }));
  }
  const out: PlanColumn[] = [];
  for (let i = 0; i < 7; i++) {
    const date = addDays(run.startDate, 7 * w + i);
    if (run.endDate && date > run.endDate) break;
    const pd = planDayOn(state, date);
    out.push({ date, day: pd && pd.run.id === run.id ? pd.day : null });
  }
  return out;
}

/** The latest decision for Day `day` of an occasion, whatever date it starts (a plan that isn't running). */
export function latestForDay(entries: ReadonlyArray<PlanEntry>, occasionId: string, day: number): PlanEntry | null {
  return resolvePlanRow(entries.filter(e => e.scope !== 'date'), occasionId, '9999-12-31', day);
}

/** One plan's use of a meal: the cells that show it, and whether its meal list keeps it. */
export interface MealUse {
  plan: MealPlan;
  /** Per occasion: the Days whose latest decision is this meal, and dated cells (today on) it decides. */
  cells: { occasion: PlanOccasion; days: number[]; dates: string[] }[];
  inList: boolean;
}

/**
 * ⭐ Where a meal is used in your plans (owner, 2026-10-18 — the Meals panel and Delete). A cell
 * counts only when the decision IN FORCE is this meal (the same rule the grid reads: the newest
 * applicable row), so a meal you replaced is not listed. Dated cells are from `today` on.
 * Archived plans are included (Delete reaches them too) — callers decide what to show.
 */
export function mealInPlans(state: PlanState & { planMeals: ReadonlyArray<{ planId: string; mealId: string }> }, mealId: string, today: string): MealUse[] {
  const out: MealUse[] = [];
  for (const plan of state.plans) {
    const cells: MealUse['cells'] = [];
    for (const occasion of occasionsOf(state, plan.id)) {
      const days: number[] = [];
      for (let d = 1; d <= plan.days; d++) if (latestForDay(state.entries, occasion.id, d)?.mealId === mealId) days.push(d);
      const dates = new Set<string>();
      for (const e of state.entries) {
        if (e.scope !== 'date' || e.occasionId !== occasion.id || e.mealId !== mealId || !e.planDate || e.planDate < today) continue;
        const pd = planDayOn(state, e.planDate);
        if (resolvePlanRow(state.entries, occasion.id, e.planDate, pd?.plan.id === plan.id ? pd.day : null)?.id === e.id) dates.add(e.planDate);
      }
      if (days.length || dates.size) cells.push({ occasion, days, dates: [...dates].sort() });
    }
    const inList = state.planMeals.some(m => m.planId === plan.id && m.mealId === mealId);
    if (cells.length || inList) out.push({ plan, cells, inList });
  }
  return out;
}

/** How many cells a meal fills across its uses (Days + dated cells). */
export const mealUseCells = (uses: ReadonlyArray<MealUse>) =>
  uses.reduce((n, u) => n + u.cells.reduce((m, c) => m + c.days.length + c.dates.length, 0), 0);

/** What a plan's info card states (owner, 2026-10-20) — read from its Day pattern, one-day changes aside. */
export interface PlanSummary {
  occasions: number;
  /** Day × occasion slots, and how many have a meal. */
  slots: number;
  filled: number;
  /** Planned calories on an average day that has anything planned; null when nothing is. */
  averageKcal: { value: number; incomplete: boolean; days: number } | null;
}

/**
 * ⭐ A plan's shape for its info card: occasions, slots filled, and an average planned day.
 * Every Day 1…N reads its LATEST decision (`latestForDay`), as the grid does on a plan that
 * isn't running; a meal that can't be found, or doesn't know its calories, marks the average
 * incomplete (a floor) — never guessed.
 */
export function planSummary(state: PlanState, meals: ReadonlyArray<Meal>, plan: MealPlan): PlanSummary {
  const occ = occasionsOf(state, plan.id);
  const byId = new Map(meals.map(m => [m.id, m]));
  let filled = 0, total = 0, days = 0, incomplete = false;
  for (let d = 1; d <= plan.days; d++) {
    const items: PlannedItem[] = [];
    for (const occasion of occ) {
      const row = latestForDay(state.entries, occasion.id, d);
      if (row?.mealId && row.portion) items.push({ occasion, mealId: row.mealId, portion: row.portion, meal: byId.get(row.mealId) ?? null });
    }
    filled += items.length;
    if (!items.length) continue;
    const kcal = plannedTotals(items).calories;
    days += 1;
    total += kcal.value;
    if (kcal.incomplete || !kcal.known) incomplete = true;
  }
  return {
    occasions: occ.length,
    slots: occ.length * plan.days,
    filled,
    averageKcal: days ? { value: Math.round(total / days), incomplete, days } : null,
  };
}
